import { GridActionsCellItem, GridRowModes } from '@mui/x-data-grid-pro';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { addCulture, deleteCulture, getCultures, resetCulture } from 'services/API/Cooperative';
import { ThemeProvider } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import { DialogContentText } from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import { displayErrorMessage } from 'utils/tools_functions';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { resetEffects } from 'services/API/Exploitation';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { customDialogTheme } from 'assets/styles/themes/Dialog/generic_dialog_mui';
import { useTranslation } from 'react-i18next';
import { StyledTooltip, resetErrorsForm } from './CustomGenericDataGridUtils';

const useDataGridCulturesActions = (
  user,
  rows,
  refreshData,
  isOrganic,
  cultures,
  errorsFormDefault,
  errorsFormCoProduct,
  errorsFormEconomicValue,
  fromCooperative,
  fromExploitation,
  exploit,
  cooperativeId,
  invertedCultures,
  checkCustomizedCulture,
  hasCustomizedCulture,
  setToggleAction,
  stackedRequestsRef,
) => {
  const [rowModesModel, setRowModesModel] = useState({});
  const [actionId, setActionId] = useState(null);
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDeleteEffectsDialog, setOpenDeleteEffectsDialog] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    if (invertedCultures.length > 0 && cultures.length > 0) {
      checkCustomizedCulture(actionId);
    }
  }, [invertedCultures, cultures, actionId, isOrganic]);

  const handleSaveClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View },
    });
  };

  const handleEditClick = (id) => () => {
    stackedRequestsRef.current.push({
      rowId: id,
    });
    setRowModesModel((prevRowModesModel) => {
      if (Object.keys(prevRowModesModel).length === 0) {
        return { [id]: { mode: GridRowModes.Edit } };
      } else {
        let resetPrevRowModesModel = {};
        for (const key in prevRowModesModel) {
          if (prevRowModesModel.hasOwnProperty(key)) {
            resetPrevRowModesModel[key] = { mode: GridRowModes.View };
            stackedRequestsRef.current = [];
          }
        }
        return { ...resetPrevRowModesModel, [id]: { mode: GridRowModes.Edit, fromAction: true } };
      }
    });
  };

  const handleCancelClick = (id) => (field) => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
    stackedRequestsRef.current = [];

    resetErrorsForm(errorsFormDefault, errorsFormEconomicValue, errorsFormCoProduct);
  };

  const handleDuplicateDialogOpen = (id) => {
    setActionId(id);
    setOpenDuplicateDialog(true);
  };

  const handleDeleteDialogOpen = async (id) => {
    setActionId(id);
    setOpenDeleteDialog(true);
  };

  const handleDeleteEffectsDialogOpen = (id) => {
    setActionId(id);
    setOpenDeleteEffectsDialog(true);
  };

  const getActions = ({ id }) => {
    const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
    if (isInEditMode) {
      return [
        <div>
          <StyledTooltip placement="top" title={t('common.save')}>
            <GridActionsCellItem
              icon={<SaveIcon sx={{ paddingLeft: '2px !important' }} />}
              label={t('common.save')}
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />
          </StyledTooltip>
          <StyledTooltip placement="top" title={t('common.cancel')}>
            <GridActionsCellItem
              icon={<CancelIcon sx={{ paddingLeft: '2px !important' }} />}
              label={t('common.cancel')}
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />
          </StyledTooltip>
        </div>,
      ];
    }
    return [
      <div>
        <StyledTooltip placement="top" title={t('action.edit')}>
          <GridActionsCellItem
            icon={<EditIcon sx={{ paddingLeft: '2px !important' }} />}
            label={t('action.edit')}
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />
        </StyledTooltip>
        {fromCooperative && (
          <React.Fragment>
            <StyledTooltip placement="top" title={t('common.duplicate')}>
              <GridActionsCellItem
                icon={<ContentCopyIcon sx={{ paddingLeft: '2px !important' }} />}
                label={t('common.duplicate')}
                onClick={() => handleDuplicateDialogOpen(id)}
                color="inherit"
              />
            </StyledTooltip>
            <StyledTooltip placement="top" title={t('common.delete')}>
              <GridActionsCellItem
                icon={<DeleteIcon sx={{ paddingLeft: '2px !important' }} />}
                label={t('common.delete')}
                onClick={() => {
                  (setToggleAction(true), handleDeleteDialogOpen(id));
                }}
                color="inherit"
              />
            </StyledTooltip>
          </React.Fragment>
        )}
        {fromExploitation && (
          <StyledTooltip placement="top" title={t('action.deleteEffects')}>
            <GridActionsCellItem
              icon={<RestartAltIcon sx={{ paddingLeft: '2px !important' }} />}
              label={t('action.deleteEffects')}
              onClick={() => handleDeleteEffectsDialogOpen(id)}
              color="inherit"
            />
          </StyledTooltip>
        )}
      </div>,
    ];
  };

  const handleDeleteClick = async () => {
    setOpenDeleteDialog(false);
    const cultureToDelete = rows.find((row) => row.id === actionId);
    try {
      if (hasCustomizedCulture) {
        await resetCulture(cultureToDelete, isOrganic);
      } else {
        await deleteCulture(cultureToDelete);
      }
      toast.success(t('common.itemDeleted', { name: cultureToDelete.name }));
      refreshData(isOrganic);
    } catch (error) {
      const errorContext = error?.response?.data?.context;
      if (errorContext?.errorCode) {
        toast.error(displayErrorMessage(errorContext?.errorCode, errorContext));
      } else {
        toast.error(displayErrorMessage('ERR_DELETE_CULTURE'));
      }
    }
  };

  const renderDeleteDialog = () => {
    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="xs" open={openDeleteDialog}>
          <DialogTitle>{t('common.warning')}</DialogTitle>
          <DialogContent dividers>{t('dialog.confirmDeleteCulture')}</DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteClick}>
              {t('common.yes')} <DoneIcon />
            </Button>
            <Button onClick={() => setOpenDeleteDialog(false)}>
              {t('common.no')} <ClearIcon />
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };

  const handleDeleteEffectsClick = async () => {
    setOpenDeleteEffectsDialog(false);
    const cultureEffectsToDelete = rows.find((row) => row.id === actionId);
    try {
      await resetEffects(exploit, cultureEffectsToDelete, isOrganic);
      toast.success(t('success.cultureEffectsDeleted', { name: cultureEffectsToDelete.name }));
      refreshData(isOrganic);
    } catch (error) {
      toast.error(displayErrorMessage('ERR_REINIT_EFFECT'));
    }
  };

  const renderDeleteEffectsDialog = () => {
    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="m" open={openDeleteEffectsDialog}>
          <DialogTitle>{t('common.warning')}</DialogTitle>
          <DialogContent dividers>{t('dialog.confirmDeleteCultureEffects')}</DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteEffectsClick}>
              {t('common.yes')} <DoneIcon />
            </Button>
            <Button onClick={() => setOpenDeleteEffectsDialog(false)}>
              {t('common.no')} <ClearIcon />
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };

  const handleDuplicateClick = async () => {
    setOpenDuplicateDialog(false);
    const cultureToDuplicate = cultures.find((culture) => culture.id === actionId);

    // Key InputCost to delete before sending culture to the back because back will not process it
    // But we need it in front for some display values
    delete cultureToDuplicate?.economicAttributeCollection?.inputCost;

    cultureToDuplicate.cropCopy = actionId;
    cultureToDuplicate.capCategory = cultureToDuplicate?.capCategory
      ? cultureToDuplicate?.capCategory?.id
      : null;
    cultureToDuplicate.constraint = cultureToDuplicate?.constraint
      ? cultureToDuplicate?.constraint?.id
      : null;
    cultureToDuplicate.name = `${cultureToDuplicate.name} copie`;
    const { id, rotationEffectN1s, rotationEffectN2s, groundTypeEffects, ...body } =
      cultureToDuplicate;
    try {
      let cooperativeId = -1;
      if (user?.role === 'SUPER_ADMIN') {
        cooperativeId = Number(localStorage.getItem('cooperative'));
      }
      await addCulture(body, cooperativeId);
      toast.success(t('success.cultureDuplicated', { name: cultureToDuplicate.name }));
      refreshData(isOrganic);
    } catch (error) {
      if (
        error.response.data.message ===
        `A culture already exists with the name ${cultureToDuplicate.name}`
      ) {
        toast.error(t('errors.cultureAlreadyExists', { name: cultureToDuplicate.name }));
      } else {
        toast.error(displayErrorMessage('ERR_ADD_CULTURE'));
      }
    }
  };

  const renderDuplicateDialog = () => {
    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="xs" open={openDuplicateDialog}>
          <DialogTitle>{t('common.warning')}</DialogTitle>
          <DialogContent dividers>
            {t('dialog.confirmDuplicateCulture')}
            <DialogContentText>
              <InfoIcon />
              {t('dialog.duplicateImpliesEffects')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDuplicateClick}>
              {t('common.yes')} <DoneIcon />
            </Button>
            <Button onClick={() => setOpenDuplicateDialog(false)}>
              {t('common.no')} <ClearIcon />
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };

  return {
    rowModesModel,
    setRowModesModel,
    getActions,
    renderDeleteDialog,
    renderDuplicateDialog,
    renderDeleteEffectsDialog,
  };
};

export default useDataGridCulturesActions;
